import React from 'react'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {BRAND_KEY} from '../brand'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    AlertIcon,
    DiscountIcon,
    GuaranteedIcon,
    ManualsIcon,
    SupportIcon,
    Icon360,
} from '../components/icons'
import {Link} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useCurrentHost from '../hooks/use-current-host'
import {useCustomPreference, useCustomPreferenceWithLocale} from '../hooks/use-custom-preference'

export const getWarrantyMonths = (preference, purchasePrice, currency, materialGroup, product) => {
    const isRefurbished =
        (product?.representedProduct?.id || product?.id)?.startsWith('X') ||
        product?.c_defaultCategoryName?.startsWith('Refurbished')
    if (!preference || `${materialGroup || ''}`.startsWith('2Q') || isRefurbished) {
        return ''
    }
    const isValidMonths = (months) => Number.isInteger(months) && months > 0
    const defaultMonths = isValidMonths(preference?.months) ? preference.months : ''

    const priceTiers = Array.isArray(preference?.priceTiers) ? preference.priceTiers : []
    const matchingTier = priceTiers.reduce((selectedTier, tier) => {
        if (
            tier?.currency !== currency ||
            !currency ||
            !Number.isFinite(tier?.minPrice) ||
            tier.minPrice < 0 ||
            !isValidMonths(tier?.months) ||
            !(Number.isFinite(purchasePrice) && purchasePrice >= tier.minPrice)
        ) {
            return selectedTier
        }

        return !selectedTier || tier.minPrice > selectedTier.minPrice ? tier : selectedTier
    }, null)

    return matchingTier?.months ?? defaultMonths
}

export const normalizeProductRegistrationCategories = (preference) => {
    let rows = preference
    if (typeof rows === 'string') {
        try {
            rows = JSON.parse(rows)
        } catch {
            return []
        }
    }
    if (!Array.isArray(rows)) rows = rows?.categories
    if (!Array.isArray(rows)) return []

    return rows.flatMap((row, index) => {
        if (Array.isArray(row)) {
            row = {
                categoryLabel: row[0],
                categoryImage: row[1],
                categoryPath: row[2],
            }
        }
        if (!row || typeof row !== 'object') return []

        const getField = (...keys) => {
            const key = keys.find((candidate) => row[candidate] !== undefined)
            return key ? row[key] : ''
        }
        const label = `${getField('categoryLabel', 'label', 'Category Label')}`.trim()
        const categoryPath = `${getField(
            'categoryPath',
            'path',
            'Category Path (SN/Legacy)',
        )}`.trim()
        if (!label || !categoryPath) return []

        const rawImage = getField('categoryImage', 'image', 'Category Image')
        const categoryImage = `${rawImage || ''}`.trim()

        return [
            {
                id: `${index}-${label}`,
                label,
                categoryPath: categoryPath.toLowerCase() === 'sn' ? 'SN' : 'Legacy',
                categoryImage: categoryImage && categoryImage !== '-' ? categoryImage : '',
                categoryMaterialGroup: `${getField('categoryMaterialGroup') || ''}`.trim(),
            },
        ]
    })
}

export const isSerialNumberRegistrationCategory = (categoryPath) => {
    const normalizedCategoryPath = `${categoryPath || ''}`.trim().toLowerCase()
    return Boolean(normalizedCategoryPath && normalizedCategoryPath !== 'legacy')
}

export const isSerialNumberRequiredForLegacy = (preference, categoryPath, materialGroup) => {
    if (`${categoryPath || ''}`.trim().toLowerCase() !== 'legacy' || !materialGroup) {
        return false
    }

    return normalizeProductRegistrationCategories(preference).some(
        (category) =>
            category.categoryPath === 'SN' &&
            category.categoryMaterialGroup
                .split(',')
                .map((group) => group.trim())
                .filter(Boolean)
                .includes(`${materialGroup}`.trim()),
    )
}

const parseDateInput = (dateInput) => {
    if (!dateInput) {
        return null
    }

    if (dateInput instanceof Date) {
        return new Date(dateInput)
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return new Date(`${dateInput}T00:00:00`)
    }

    const isNoSeparatorFormat = /^\d{14}$/.test(dateInput)
    if (isNoSeparatorFormat) {
        const [, year, month, day] = dateInput.match(/(\d{4})(\d{2})(\d{2})/).map(Number)
        return new Date(year, month - 1, day)
    }

    return new Date(dateInput)
}

const formatDate = (dateInput) => {
    const parsedDate = parseDateInput(dateInput)
    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
        return ''
    }

    return parsedDate.toLocaleDateString('it-IT')
}

const parsePriceInput = (priceInput) => {
    if (typeof priceInput === 'number') {
        return Number.isFinite(priceInput) ? priceInput : null
    }

    if (typeof priceInput !== 'string') {
        return null
    }

    const normalizedPrice = priceInput.trim().replace(/\s/g, '').replace(',', '.')
    const parsedPrice = Number.parseFloat(normalizedPrice)

    return Number.isFinite(parsedPrice) ? parsedPrice : null
}

const getWarrantyDateFromPurchase = (purchaseDateInput, preference, purchasePrice, currency) => {
    const purchaseDate = parseDateInput(purchaseDateInput)
    if (!purchaseDate || Number.isNaN(purchaseDate.getTime())) {
        return ''
    }

    const parsedPurchasePrice = parsePriceInput(purchasePrice)
    const warrantyMonths = getWarrantyMonths(preference, parsedPurchasePrice, currency)
    if (warrantyMonths === '') {
        return ''
    }

    const warrantyEndDate = new Date(purchaseDate)
    warrantyEndDate.setMonth(warrantyEndDate.getMonth() + warrantyMonths)

    return formatDate(warrantyEndDate)
}

export const useProductRegistrationData = ({
    product,
    warrantyExpirationDateKut,
    purchaseDate,
    purchasePrice,
    currency,
    materialGroup,
} = {}) => {
    const {locale, buildUrl} = useMultiSite()
    const warrantyExpirationDates = useCustomPreference('warrantyExpirationDates')
    const productRegistrationCategoriesPreference =
        useCustomPreferenceWithLocale('prodRegCategories')
    const {getHost} = useCurrentHost()
    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const warrantyDate =
        warrantyExpirationDateKut !== undefined
            ? formatDate(warrantyExpirationDateKut)
            : getWarrantyDateFromPurchase(
                  purchaseDate,
                  warrantyExpirationDates,
                  purchasePrice,
                  currency,
              )
    const showWarranty =
        Boolean(warrantyExpirationDates) &&
        !`${materialGroup || ''}`.startsWith('2Q') &&
        Boolean(warrantyDate)

    const findSerialNumberUrl = `${getHost()}${buildUrl('/e/find-serial-number')}`

    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    let skuImage

    switch (BRAND_KEY) {
        case 'kenwood':
            skuImage = getAssetUrl(`static/img/product-registration/skuImage-kenwood.png`)
            break
        case 'braun':
            skuImage = getAssetUrl(`static/img/product-registration/skuImage-braun.png`)
            break
        default: // delonghi image is fallback
            skuImage = getAssetUrl(`static/img/product-registration/skuImage-delonghi.png`)
    }

    let modelNumberImage

    switch (BRAND_KEY) {
        case 'kenwood':
            modelNumberImage = getAssetUrl(
                `static/img/product-registration/modelNumber-kenwood.png`,
            )
            break
        case 'nutribullet':
            modelNumberImage = getAssetUrl(
                `static/img/product-registration/modelNumber-nutribullet.png`,
            )
            break
        case 'braun':
            modelNumberImage = getAssetUrl(`static/img/product-registration/modelNumber-braun.png`)
            break
        default: // delonghi image is fallback
            modelNumberImage = getAssetUrl(
                `static/img/product-registration/modelNumber-delonghi.png`,
            )
    }

    const modelNumberImageLegacy =
        BRAND_KEY === 'delonghi'
            ? getAssetUrl(`static/img/product-registration/modelNumberLegacy-delonghi.png`)
            : modelNumberImage

    const skuTitle = (
        <FormattedMessage
            id="product_registration.sku_title"
            defaultMessage="Where can i find the SKU code?"
        />
    )
    const skuDescription = (
        <FormattedMessage
            id="product_registration.sku_description"
            defaultMessage="You can find the SKU code on the box of your product, usually on the long side at the top left."
        />
    )
    const modelTitle = (
        <FormattedMessage
            id="product_registration.model_title"
            defaultMessage="Where can i find the Model Number?"
        />
    )
    const modelDescription = (
        <FormattedMessage
            id="product_registration.model_description"
            defaultMessage="You can find the Model Number am auctor eget quam vitae fringilla. Have a different model? Search for it <u>here</u>."
            values={{
                u: (chunks) => (
                    <u>
                        <a
                            href="https://www.delonghi.com/it-it/support/find-serial-number"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {chunks}
                        </a>
                    </u>
                ),
            }}
        />
    )

    const serialNumberTitle1 = (
        <FormattedMessage
            id="product_registration.serial_number_title_1"
            defaultMessage="If you still have the product box"
        />
    )
    const serialNumberDescription1 = (
        <FormattedMessage
            id="product_registration.serial_number_description_1"
            defaultMessage={`Scan the QR code next to the "Register Now" message with your phone's camera, or manually enter the alphanumeric code (up to 19 digits long) that you can find inside the label.`}
        />
    )
    const serialNumberTitle2 = (
        <FormattedMessage
            id="product_registration.serial_number_title_2"
            defaultMessage="If you no longer have the box"
        />
    )
    const serialNumberDescription2 = (
        <FormattedMessage
            id="product_registration.serial_number_description_2"
            defaultMessage="Scan the data matrix (which looks like a QR code) located behind the water tank, or enter the serial number manually. <u>Where to find serial number page?</u>."
            values={{
                u: (chunks) => (
                    <Link
                        href={findSerialNumberUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="button.text.text-default"
                        textDecoration="underline"
                    >
                        {chunks}
                    </Link>
                ),
            }}
        />
    )

    const modelNumberTitle1 = (
        <FormattedMessage
            id="product_registration.serial_number_title_1"
            defaultMessage="If you still have the product box"
        />
    )
    const modelNumberDescription1 = (
        <FormattedMessage
            id="product_registration.model_number_description_1"
            defaultMessage="You can find the SKU code on the label of your product box, usually on the top side."
        />
    )
    const modelNumberTitle2 = (
        <FormattedMessage
            id="product_registration.serial_number_title_2"
            defaultMessage="If you no longer have the box"
        />
    )
    const modelNumberDescription2 = (
        <FormattedMessage
            id="product_registration.model_number_description_2"
            defaultMessage="The Model Number is typically printed on the identification plate at the base of your machine."
        />
    )

    const categories = normalizeProductRegistrationCategories(
        productRegistrationCategoriesPreference,
    ).map((category) => ({
        ...category,
        image: category.categoryImage,
    }))

    const cardsData = [
        {
            icon: GuaranteedIcon,
            title: (
                <FormattedMessage
                    id="product_registration.activate_warranty_title"
                    defaultMessage="Secure your warranty"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.activate_warranty_description"
                    defaultMessage="Keep your guarantee always within reach"
                />
            ),
            testId: 'activate-warranty-card',
        },
        {
            icon: Icon360,
            title: (
                <FormattedMessage
                    id="energy_label.product_information"
                    defaultMessage="Product information"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.icon360_description"
                    defaultMessage="Easy access to your product information and helpful tips"
                />
            ),
            testId: 'product-info-card',
        },
        {
            icon: SupportIcon,
            title: (
                <FormattedMessage
                    id="product_registration.supportIcon_title"
                    defaultMessage="Dedicated support"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.supportIcon_description"
                    defaultMessage="Direct access to our dedicated product service and support teams"
                />
            ),
            testId: 'support-card',
        },
        {
            icon: DiscountIcon,
            title: (
                <FormattedMessage
                    id="product_registration.discountIcon_title"
                    defaultMessage="Exclusive offers"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.discountIcon_description"
                    defaultMessage="Exclusive offers with newsletter sign up"
                />
            ),
            testId: 'discount-card',
        },
    ].filter(Boolean)

    const cardsConfirmationData = [
        showWarranty && {
            icon: GuaranteedIcon,
            title: (
                <FormattedMessage
                    id="product_registration.warranty_is_active"
                    defaultMessage="Your Warranty is Active"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.warranty_is_active_description"
                    defaultMessage="Your product legal warranty ends on: {warrantyDate}."
                    values={{
                        warrantyDate,
                    }}
                />
            ),
            testId: 'your-warranty-card',
            additionalDescription: (
                <FormattedMessage
                    id="product_registration.warranty_disclaimer"
                    defaultMessage="You can view or edit your product registration data in your <u>My Account</u>"
                    values={{
                        u: (chunks) => (
                            <u>
                                <a
                                    href={`/${locale?.alias}/account/registered-products`}
                                    rel="noopener noreferrer"
                                >
                                    {chunks}
                                </a>
                            </u>
                        ),
                    }}
                />
            ),
        },
        {
            icon: ManualsIcon,
            title: (
                <FormattedMessage
                    id="product_registration.need_help_title"
                    defaultMessage="Need some help with your product?"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.need_help_description"
                    defaultMessage=" Visit your product <u>support center</u>."
                    values={{
                        u: (chunks) => (
                            <u>
                                <a
                                    href={`/${locale?.alias}/s/${
                                        product?.representedProduct?.c_subModelIdentifier ||
                                        product?.c_subModelIdentifier ||
                                        product?.productId
                                    }`}
                                    rel="noopener noreferrer"
                                >
                                    {chunks}
                                </a>
                            </u>
                        ),
                    }}
                />
            ),
            testId: 'need-help-card',
        },
        {
            icon: AlertIcon,
            title: (
                <FormattedMessage
                    id="product_registration.issues_with_proof_of_purchase_title"
                    defaultMessage="Issues with your proof of purchase"
                />
            ),
            description: (
                <FormattedMessage
                    id="product_registration.issues_with_proof_of_purchase_description"
                    defaultMessage="We had some issues validating your proof of purchase. Please visit your <u>Registered Products</u> area to review this."
                    values={{
                        u: (chunks) => (
                            <u>
                                <a
                                    href={`/${locale?.alias}/account/registered-products`}
                                    rel="noopener noreferrer"
                                >
                                    {chunks}
                                </a>
                            </u>
                        ),
                    }}
                />
            ),
            testId: 'issues-with-proof-of-purchase-card',
        },
    ].filter(Boolean)

    return {
        skuImage,
        modelNumberImage,
        modelNumberImageLegacy,
        skuTitle,
        skuDescription,
        modelTitle,
        modelDescription,
        serialNumberTitle1,
        serialNumberDescription1,
        serialNumberTitle2,
        serialNumberDescription2,
        modelNumberTitle1,
        modelNumberDescription1,
        modelNumberTitle2,
        modelNumberDescription2,
        webPageSchemaMarkup,
        categories,
        cardsData,
        cardsConfirmationData,
    }
}
